import { BodyPrimary } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { PaginationFilter } from '@liveauctioneers/caterwaul-components/types/PaginationFilter';
import { FormattedMessage, FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import { Select } from '@liveauctioneers/hammer-ui-core/select';
import { Component } from 'react';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';

import CategoryAd from '@/components/Ads/CategoryAd';
import PageTitle from '@/components/PageTitle/PageTitle';
import Pagination from '@/components/Pagination/hammer-pagination/Pagination';
import WonItemsCard from '@/pages/WonItems/components/WonItemsCard/WonItemsCard';
import { LeaderBoardAdTypes } from '@/redux/modules/catalog/ad/catalogAd.types';
import { WonCatalog } from '@/types/WonCatalog';

import EOARemovalResponseAction from './EOARemovalResponseAction';
import WonItemsPageHeaderMeta from './WonItemsPageHeaderMeta';

const VIEWS_TO_SHOW = [10, 25, 50, 100];

export type WonItemsPageProps = {
    bidderIsSuspended: boolean;
    filter: string;
    filterOptions: any[];
    handleFilterChange: (event: { value: string }) => void;
    onPageChange: Function;
    reviews: any[];
    wonCatalogs: WonCatalog[];
    wonItemsViewData: PaginationFilter;
};

type State = {
    addCatalogItems: any;
};

export default class WonItemsPage extends Component<WonItemsPageProps, State> {
    state = {
        addCatalogItems: [],
    };

    shouldComponentUpdate = (nextProps: WonItemsPageProps, nextState: State) =>
        !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);

    mapWonCatalogs = (wonCatalog: WonCatalog) => {
        const { reviews } = this.props;

        const reviewed = Boolean(reviews.find((review) => review.catID === wonCatalog.catalogId && review.reviewed));

        return (
            <WonItemsCard
                isBidderSuspended={this.props.bidderIsSuspended}
                key={wonCatalog.catalogId}
                reviewed={reviewed}
                wonCatalog={wonCatalog}
            />
        );
    };

    render() {
        const { filter, filterOptions, handleFilterChange, onPageChange, wonCatalogs, wonItemsViewData } = this.props;

        const adId = 'won-items';
        const adPageName = `leaderboard-${adId}`;

        return (
            <>
                <CategoryAd
                    adType={LeaderBoardAdTypes[adId]}
                    pageName={adPageName}
                />
                <Container>
                    <WonItemsPageHeaderMeta />
                    <EOARemovalResponseAction />
                    <PageTitle title={<FormattedMessage id="won_items" />} />
                    <Pagination
                        filter={
                            <FilterContainer>
                                <h5 data-testid="orderCount">
                                    <FormattedMessage
                                        id="wonitems.orderCount"
                                        values={{
                                            formattedNumber: (
                                                <FormattedNumber
                                                    key="formatted_number"
                                                    value={wonItemsViewData.totalRecords || 0}
                                                />
                                            ),
                                            totalOrders: wonItemsViewData.totalRecords || 0,
                                        }}
                                    />
                                </h5>
                                <Select
                                    className="w-[230px]"
                                    isInsideLabel
                                    labelText={
                                        <>
                                            <FormattedMessage id="dispute_status" />:
                                        </>
                                    }
                                    onChange={handleFilterChange}
                                    options={filterOptions}
                                    placeholder={<FormattedMessage id="select" />}
                                    small
                                    value={filterOptions.find((op) => op.value === filter)}
                                />
                            </FilterContainer>
                        }
                        onChange={onPageChange}
                        pageSizeOptions={VIEWS_TO_SHOW}
                        paginationFilter={wonItemsViewData}
                        shouldModifyQueryParams
                    >
                        {wonCatalogs.length !== 0 && wonCatalogs.map(this.mapWonCatalogs)}

                        {!wonCatalogs.length && (
                            <EmptyMessageContainer>
                                <BodyPrimary>
                                    {filter === 'ALL' ? (
                                        <FormattedMessage id="no_won_items" />
                                    ) : (
                                        <FormattedMessage id="no_disputes" />
                                    )}
                                </BodyPrimary>
                            </EmptyMessageContainer>
                        )}
                    </Pagination>
                </Container>
            </>
        );
    }
}

const Container = styled.div`
    min-height: 400px;
    padding: 0 20px;
    position: relative;
    max-width: ${({ theme }) => theme.breakpoints.pageWidth};
    margin: 0 auto 50px;
`;

const FilterContainer = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 15px;
`;

const EmptyMessageContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    padding: 50px;
`;
